<template>
  <header class="app-header animate__animated animate__fadeIn" :class="{ 
    'expanded': showDateSelector && showDateSelectors,
    'no-date-selectors': !showDateSelectors 
  }">
    <div class="header-content">
      <div class="logo animate__animated animate__fadeInLeft">
        <router-link :to="logoTarget">
          <div class="logo-img" :class="{ spinning: isLoading, 'transition-spinning': isTransitioning }" v-html="logoIcon"></div>
          <div class="logo-text-container">
            <span class="logo-text">GRA Platform</span>
          </div>
        </router-link> 
      </div>
      
      <!-- Joke ticker positioned below logo -->
      <div class="joke-ticker-container" @mouseenter="startJokeTicker" @mouseleave="stopJokeTicker">
        <div class="joke-ticker" :class="{ 'active': isJokeActive }">
          <div class="joke-text" :style="{ transform: `translateX(${jokePosition}px)` }">
            {{ currentJoke }}
          </div>
        </div>
      </div>
      
      <!-- DateRangeSummary component - only visible when authenticated and on pages with date range filtering -->
      <transition name="date-summary">
        <DateRangeSummary
          v-if="authStore.isAuthenticated && showDateSelectors"
          class="date-range-summary-desktop"
          :startDate="dateRangeStore.startDate?.value || ''"
          :endDate="dateRangeStore.endDate?.value || ''"
          @previous="dateRangeStore.goToPreviousDay"
          @next="dateRangeStore.goToNextDay"
          @update:startDate="(val: string) => dateRangeStore.updateDateRange(val, dateRangeStore.endDate?.value || '')"
          @update:endDate="(val: string) => dateRangeStore.updateDateRange(dateRangeStore.startDate?.value || '', val)"
          @update="handleDateRangeUpdate"
          @toggleDateSelector="handleToggleDateSelector"
          @share="handleShareData"
          @openShareModal="openShareModal"
        />
      </transition>
      
      <nav class="nav-links animate__animated animate__fadeInRight">
        <router-link v-if="authStore.isAuthenticated && route.name !== 'home'" to="/dashboard" class="nav-link home-button">
          <span class="nav-icon"><Icon name="home" size="md" aria-label="Home" /></span>
          <span class="nav-text">Home</span>
        </router-link>
        
        <!-- Dropdown Menu for other options -->
        <div class="dropdown-menu" :class="{ 'dropdown-open': isDropdownOpen }">
          <button @click="toggleDropdown" class="nav-link dropdown-toggle">
            <div v-if="authStore.isAuthenticated && userProfilePicture" class="user-avatar">
              <img 
                :src="userProfilePicture.url" 
                alt="Profile Picture"
                class="profile-image"
                @error="handleProfileImageError"
              />
            </div>
            <span v-else class="nav-icon"><Icon name="menu" size="md" aria-label="Menu" /></span>
            <span class="nav-text">Menu</span>
            <span class="dropdown-arrow" :class="{ 'rotated': isDropdownOpen }">▼</span>
          </button>
          
          <div class="dropdown-content">
            <!-- Authenticated user menu items -->
            <template v-if="authStore.isAuthenticated || authStore.token">
              <router-link to="/profile" @click="closeDropdown" class="dropdown-item profile-link">
                <span class="nav-icon"><Icon name="profile" size="md" aria-label="Profile" /></span>
                <span class="nav-text">Profile</span>
                <ProfileUpdateBubble :isMenuOpen="isDropdownOpen" />
              </router-link>
              <router-link to="/drug-search" @click="closeDropdown" class="dropdown-item drugs-button">
                <span class="nav-icon"><Icon name="search" size="md" aria-label="Meds" /></span>
                <span class="nav-text">Meds</span>
              </router-link>
              <router-link v-if="featuresStore.isDrugInteractionsEnabled" to="/drug-interactions" @click="closeDropdown" class="dropdown-item interactions-button">
                <span class="nav-icon"><Icon name="interactions" size="md" aria-label="Check Interactions" /></span>
                <span class="nav-text">Check Interactions</span>
              </router-link>
              <router-link to="/conditions" @click="closeDropdown" class="dropdown-item conditions-link">
                <span class="nav-icon"><Icon name="conditions" size="md" aria-label="Conditions" /></span>
                <span class="nav-text">Conditions</span>
              </router-link>
              <router-link v-if="authStore.user?.is_admin" to="/admin" @click="closeDropdown" class="dropdown-item admin-button">
                <span class="nav-icon"><Icon name="admin" size="md" aria-label="Admin" /></span>
                <span class="nav-text">Admin</span>
              </router-link>
              <button @click="handleLogoutClick" class="dropdown-item logout">
                <span class="nav-icon"><Icon name="logout" size="md" aria-label="Logout" /></span>
                <span class="nav-text">Logout</span>
              </button>
            </template>
            
            <!-- Non-authenticated user menu items -->
            <template v-else>
              <router-link to="/auth" @click="closeDropdown" class="dropdown-item login">
                <span class="nav-icon"><Icon name="login" size="md" aria-label="Login" /></span>
                <span class="nav-text">Login</span>
              </router-link>
              <a @click="handlePlatformsClick" class="dropdown-item platforms-button">
                <span class="nav-icon"><Icon name="platforms" size="md" aria-label="Platforms" /></span>
                <span class="nav-text">Platforms</span>
              </a>
              <router-link to="/line-of-business" @click="closeDropdown" class="dropdown-item line-of-business-button">
                <span class="nav-icon"><Icon name="line-of-business" size="md" aria-label="Line of Business" /></span>
                <span class="nav-text">Line of Business</span>
              </router-link>
              <router-link to="/about" @click="closeDropdown" class="dropdown-item about-button">
                <span class="nav-icon"><Icon name="about" size="md" aria-label="About Us" /></span>
                <span class="nav-text">About Us</span>
              </router-link>
              <router-link to="/feedback" @click="closeDropdown" class="dropdown-item contact-button">
                <span class="nav-icon"><Icon name="contact" size="md" aria-label="Contact Us" /></span>
                <span class="nav-text">Contact Us</span>
              </router-link>
            </template>
          </div>
        </div>
        <!-- <router-link v-if="!authStore.isAuthenticated && !authStore.token && isPricingEnabled" to="/pricing" class="nav-link pricing">
          <span class="nav-icon" v-html="pricingIcon" aria-hidden="true"></span>
          <span class="nav-text">Pricing</span>
        </router-link> -->
        <router-link v-if="!authStore.isAuthenticated && !authStore.token && route.name === 'auth' && (!route.query.view || route.query.view !== 'register') && !featuresStore.isRegistrationDisabled" to="/auth?view=register" class="nav-link signup">
          <span class="nav-icon"><Icon name="login" size="md" aria-label="Sign Up" /></span>
          <span class="nav-text">Sign Up</span>
        </router-link>
      </nav>
    </div>
    
    <!-- Horizontal separator line -->
    <div v-if="authStore.isAuthenticated && showDateSelectors" class="header-separator"></div>
    
    <!-- DateRangeSummary for mobile - positioned below other components -->
    <div class="header-mobile-section" v-if="authStore.isAuthenticated && showDateSelectors">
      <transition name="date-summary">
        <DateRangeSummary
          class="date-range-summary-mobile"
          :startDate="dateRangeStore.startDate?.value || ''"
          :endDate="dateRangeStore.endDate?.value || ''"
          @previous="dateRangeStore.goToPreviousDay"
          @next="dateRangeStore.goToNextDay"
          @update:startDate="(val: string) => dateRangeStore.updateDateRange(val, dateRangeStore.endDate?.value || '')"
          @update:endDate="(val: string) => dateRangeStore.updateDateRange(dateRangeStore.startDate?.value || '', val)"
          @update="handleDateRangeUpdate"
          @toggleDateSelector="handleToggleDateSelector"
          @share="handleShareData"
          @openShareModal="openShareModal"
        />
      </transition>
    </div>
    
    <!-- DateRangeSelector with Animation -->
    <transition name="date-selector">
      <div v-if="showDateSelector && showDateSelectors" class="date-selector-wrapper">
        <DateRangeSelector
          v-if="authStore.isAuthenticated"
          :startDate="dateRangeStore.startDate?.value || ''"
          :endDate="dateRangeStore.endDate?.value || ''"
          @update:startDate="(val: string) => dateRangeStore.updateDateRange(val, dateRangeStore.endDate?.value || '')"
          @update:endDate="(val: string) => dateRangeStore.updateDateRange(dateRangeStore.startDate?.value || '', val)"
          @update="handleDateRangeUpdate"
        />
      </div>
    </transition>
  </header>

  <!-- Share Data Modal -->
  <teleport to="body">
    <ShareDataModal
      ref="shareModal"
      :isOpen="isShareModalOpen"
      :startDate="dateRangeStore.startDate?.value || ''"
      :endDate="dateRangeStore.endDate?.value || ''"
      :userEmail="authStore.user?.email || ''"
      @close="closeShareModal"
      @share="handleShareData"
    />
  </teleport>
</template>

<script setup lang="ts">
import { useAuthStore } from '../stores/auth'
import { useRouter, useRoute } from 'vue-router'
import { getIcon } from '../utils/icons'
import DateRangeSummary from './DateRangeSummary.vue'
import DateRangeSelector from './DateRangeSelector.vue'
import ShareDataModal from './ShareDataModal.vue'
import { useDateRangeStore } from '../stores/dateRangeStore'
import { useFeaturesStore } from '../stores/features'
import { ref, computed, nextTick } from 'vue'
import ProfileUpdateBubble from './ProfileUpdateBubble.vue'
import Icon from './Icon.vue'
import apiService from '../services/apiService'
// import type { FeatureFlags } from '../services/apiService'

let authStore: any
let dateRangeStore: any
let featuresStore: any
try {
  authStore = useAuthStore()
} catch (_e) {
  authStore = {
    isAuthenticated: true,
    user: { email: 'test@example.com' },
    logout: async () => {}
  }
}
try {
  dateRangeStore = useDateRangeStore()
} catch (_e) {
  const v = (val: string) => ({ value: val })
  dateRangeStore = {
    startDate: v('2024-01-01T00:00:00Z'),
    endDate: v('2024-01-31T23:59:59Z'),
    activeTab: v('substances'),
    updateActiveTab: (_: string) => {},
    updateDateRange: (_s: string, _e: string) => {},
    goToPreviousDay: () => {},
    goToNextDay: () => {}
  }
}
try {
  featuresStore = useFeaturesStore()
} catch (_e) {
  featuresStore = {
    isFunModeEnabled: true,
    isMusicEnabled: true,
    isPricingEnabled: true  // Default to enabled if store fails to load
  }
}
const router = useRouter()
const route = useRoute()
// Use gauge icon (matching landing view style)
const logoIcon = getIcon('GAUGE')

// DateRangeSelector visibility state
const showDateSelector = ref(false)

// Share modal state
const isShareModalOpen = ref(false)
const shareModal = ref<InstanceType<typeof ShareDataModal> | null>(null)

// Dropdown menu state
const isDropdownOpen = ref(false)

// Profile picture state
const userProfilePicture = ref<any>(null)

// Load user profile picture with automatic Gravatar fallback
const loadUserProfilePicture = async () => {
  if (!authStore.isAuthenticated) return
  
  try {
    const response = await apiService.get('/profile-picture')
    if (response.has_photo) {
      userProfilePicture.value = response
    } else {
      // This should not happen anymore since backend automatically provides Gravatar
      userProfilePicture.value = null
    }
  } catch (error) {
    console.error('Error loading profile picture:', error)
    userProfilePicture.value = null
  }
}

// Handle profile image error
const handleProfileImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  console.log('Profile picture failed to load:', img.src)
  userProfilePicture.value = null
}

// Joke ticker state
const isJokeActive = ref(false)
const jokePosition = ref(0)
const currentJoke = ref('')
const jokeInterval = ref<number | null>(null)

// Health and medication jokes
const jokes = [
  "💊 Why don't pills ever get lonely? Because they always come in pairs!",
  "🏥 What do you call a doctor who's always on time? A punctual physician!",
  "💉 Why did the medicine go to therapy? It had too many side effects!",
  "🩺 What's a doctor's favorite type of music? Prescription music!",
  "💊 Why don't vitamins ever get tired? Because they're always supplementing!",
  "🏥 What do you call a doctor who's also a magician? A prescriptionist!",
  "💉 Why did the thermometer break up with the stethoscope? It couldn't handle the pressure!",
  "🩺 What's a nurse's favorite type of coffee? Decaf - it's less stimulating!",
  "💊 Why don't pills ever get lost? Because they always know their dosage!",
  "🏥 What do you call a doctor who's also a chef? A gastro-enterologist!",
  "💉 Why did the bandage go to the party? It wanted to stick around!",
  "🩺 What's a doctor's favorite type of exercise? Prescription strength training!",
  "💊 Why don't medicines ever get cold? Because they're always in capsules!",
  "🏥 What do you call a doctor who's also a musician? A cardio-logist!",
  "💉 Why did the syringe go to school? It wanted to get a shot at education!"
]


// Feature flags state
// const features = ref<FeatureFlags>({ email_verification_enabled: false, light_mode_disabled: false })

// Loading state for initial page load to spin the logo icon
const isLoading = ref(true)

// Transition state for page navigation
const isTransitioning = ref(false)


// Computed property to determine if date selectors should be shown
const showDateSelectors = computed(() => {
  // Only show date selectors on pages that support date range filtering
  const pagesWithDateRange = ['home', 'admin']
  const isOnDateRangePage = pagesWithDateRange.includes(route.name as string)
  
  // Hide date selectors when MySubstances tab is active
  const isMySubstancesTabActive = dateRangeStore.activeTab.value === 'my-substances'
  
  return isOnDateRangePage && !isMySubstancesTabActive
})

const handleDateRangeUpdate = () => {
  // This will trigger a global update that all components can listen to
  // Individual components can watch the dateRangeStore for changes
}

const handleToggleDateSelector = () => {
  showDateSelector.value = !showDateSelector.value
  
  // Emit event to notify parent about header state change
  const event = new CustomEvent('headerToggle', {
    bubbles: true,
    detail: { expanded: showDateSelector.value }
  })
  document.dispatchEvent(event)
}

const handleShareData = async (data: { shareMode: string, email?: string, options: { intake: boolean, schedule: boolean, journal: boolean, goals: boolean, aiInsights?: boolean }, sendCopyToSelf: boolean }) => {
  try {
    const requestData: any = {
      shareMode: data.shareMode,
      options: data.options,
      startDate: dateRangeStore.startDate?.value || '',
      endDate: dateRangeStore.endDate?.value || '',
      sendCopyToSelf: data.sendCopyToSelf
    }
    
    if (data.shareMode === 'email' && data.email) {
      requestData.email = data.email
    }
    
    const response = await apiService.post('/share-data', requestData);

    // Show success message in the modal
    shareModal.value?.showSuccess(response.message || 'Data shared successfully!')
  } catch (error) {
    if (error instanceof Error) {
      shareModal.value?.showError(error.message)
    } else {
      shareModal.value?.showError('Failed to share data. Please try again.')
    }
    console.error('Error sharing data:', error)
  }
}

const openShareModal = () => {
  isShareModalOpen.value = true
}


const handleLogoutClick = async () => {
  await handleLogout()
  closeDropdown()
}

const closeShareModal = () => {
  isShareModalOpen.value = false
}

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const closeDropdown = () => {
  isDropdownOpen.value = false
}

const handlePlatformsClick = async () => {
  closeDropdown()
  
  // If already on landing page, scroll to features section directly
  if (route.name === 'landing' || route.path === '/') {
    nextTick(() => {
      const featuresSection = document.getElementById('features')
      if (featuresSection) {
        featuresSection.scrollIntoView({ behavior: 'smooth' })
      }
    })
  } else {
    // Navigate to landing page with hash - router's scrollBehavior will try to handle it
    await router.push('/#features')
    
    // Wait for route to actually change
    let retries = 0
    while ((route.name !== 'landing' && route.path !== '/') && retries < 20) {
      await new Promise(resolve => setTimeout(resolve, 50))
      retries++
    }
    
    // Wait for route to change and component to mount
    await nextTick()
    
    // Wait for browser to be ready to render
    await new Promise(resolve => requestAnimationFrame(resolve))
    await new Promise(resolve => requestAnimationFrame(resolve))
    
    // Additional delay to ensure page has loaded and rendered
    await new Promise(resolve => setTimeout(resolve, 600))
    
    // Manually scroll to features (router's scrollBehavior may not work during navigation)
    const scrollToFeatures = async (maxRetries = 40) => {
      for (let i = 0; i < maxRetries; i++) {
        const featuresSection = document.getElementById('features')
        if (featuresSection) {
          // Ensure we're at the top first, then scroll
          window.scrollTo({ top: 0, behavior: 'auto' })
          await new Promise(resolve => setTimeout(resolve, 100))
          featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          return true
        }
        // Wait before retrying (longer delay for first few attempts)
        const delay = i < 10 ? 100 : 50
        await new Promise(resolve => setTimeout(resolve, delay))
      }
      return false
    }
    
    const scrolled = await scrollToFeatures()
    
    // If manual scroll didn't work, try using window.location.hash as fallback
    if (!scrolled) {
      window.location.hash = '#features'
      await new Promise(resolve => setTimeout(resolve, 300))
      const featuresSection = document.getElementById('features')
      if (featuresSection) {
        featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }
}

// Close dropdown when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as Element
  if (!target.closest('.dropdown-menu')) {
    closeDropdown()
  }
}

// Add event listener for clicking outside
import { onMounted, onUnmounted, watch } from 'vue'
onMounted(async () => {
  // Load public feature flags
  // try {
  //   features.value = await apiService.get('/features', undefined, { skipAuth: true })
  // } catch {}
  // If maintenance full-lock is enabled, redirect immediately (unless user is admin)
  try {
    const m = await apiService.getMaintenance()
    if (m?.enabled && (m as any).full_lock) {
      // Allow admins to bypass maintenance mode
      const isAdmin = authStore.isAuthenticated && authStore.user?.is_admin
      if (!isAdmin) {
        const q = m.message ? `?msg=${encodeURIComponent(m.message)}` : ''
        router.replace(`/maintenance-mode${q}`)
        return
      }
    }
  } catch {}
  // Register outside click handler
  document.addEventListener('click', handleClickOutside)

  // Load user profile picture
  await loadUserProfilePicture()

  // Stop the spinner after initial mount/settle
  setTimeout(() => { isLoading.value = false }, 800)
})

// Watch for route changes to trigger transition animation
watch(() => route.path, (newPath, oldPath) => {
  if (oldPath && newPath !== oldPath) {
    isTransitioning.value = true
    // Stop transition animation after 2 seconds
    setTimeout(() => {
      isTransitioning.value = false
    }, 2000)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  stopJokeTicker() // Clean up joke ticker
})

// Logo should always navigate to the public landing page for consistency
const logoTarget = computed(() => '/')

async function handleLogout() {
  await authStore.logout()
  router.push('/auth')
}

// Joke ticker functions
function startJokeTicker() {
  if (!featuresStore.isFunModeEnabled || featuresStore.isReduceMotionEnabled || isJokeActive.value) return
  
  isJokeActive.value = true
  currentJoke.value = jokes[Math.floor(Math.random() * jokes.length)]
  jokePosition.value = 400 // Start from right side
  
  // Animate the joke scrolling from right to left (slower speed)
  jokeInterval.value = window.setInterval(() => {
    jokePosition.value -= 0.8 // Reduced speed from 2 to 0.8
    if (jokePosition.value < -400) {
      // Reset to right side and pick new joke
      jokePosition.value = 400
      currentJoke.value = jokes[Math.floor(Math.random() * jokes.length)]
    }
  }, 16) // ~60fps
}

function stopJokeTicker() {
  isJokeActive.value = false
  if (jokeInterval.value) {
    clearInterval(jokeInterval.value)
    jokeInterval.value = null
  }
  jokePosition.value = 0
}
</script>

<style scoped>
@import 'https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css';

.app-header {
  background: rgba(15, 15, 14, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.18);
  min-height: 72px;
  max-height: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  animation-duration: 0.5s;
  overflow: visible; /* Ensure bubbles are not clipped */
}

.app-header.expanded {
  max-height: 240px; /* Large enough to accommodate the DateRangeSelector with buttons */
  justify-content: flex-start;
}

.app-header.no-date-selectors {
  max-height: 72px; /* Normal height when date selectors are hidden */
}

.header-content {
  width: 100%;
  max-width: 1200px; /* Align with page content width */
  margin: 0 auto;
  padding-left: 1rem; /* Match inner content spacing */
  padding-right: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1; /* Allow content to fill available vertical space */
  flex-shrink: 0;
  overflow: visible; /* Ensure bubbles are not clipped */
  position: relative;
  z-index: 20; /* Ensure content is above mobile section */
}

.logo {
  animation-duration: 0.5s;
}

.logo a {
  font-size: 2rem;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  font-weight: 800;
  color: var(--text-color) !important; /* Keep consistent like other pages */
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: none; /* Avoid size/color flicker after animations */
  cursor: pointer;
}

.logo a:hover,
.logo a:active,
.logo a:visited {
  color: var(--text-color) !important;
  transform: none;
}

.logo a:hover .logo-img {
  filter: drop-shadow(0 2px 6px rgba(0,0,0,0.35));
}

.logo-img {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.logo-img.spinning {
  animation: spin 0.9s linear infinite;
}

.logo-img.transition-spinning {
  animation: transitionSpin 2s ease-in-out;
}

.logo-text-container {
  position: relative;
  overflow: visible;
  height: auto;
  min-height: 2rem;
  display: flex;
  align-items: center;
}

.logo-text {
  color: rgba(249, 240, 226, 1);
  transition: opacity 0.3s ease;
  white-space: nowrap;
}

.logo-img :deep(svg) {
  color: #FFFFFF;
  filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.3));
}

.logo-img :deep(svg .gauge-needle) {
  transform-origin: 12px 12px;
  animation: gaugePulse 1.5s ease-in-out infinite;
}

.logo-img.spinning :deep(svg),
.logo-img.transition-spinning :deep(svg) {
  color: #FFFFFF;
  filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.3));
}

.logo-img.spinning :deep(svg .gauge-needle),
.logo-img.transition-spinning :deep(svg .gauge-needle) {
  animation: gaugePulse 2s ease-in-out infinite, spin 0.9s linear infinite;
}

/* Joke ticker container positioned below logo */
.joke-ticker-container {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 2rem;
  overflow: hidden;
  pointer-events: none;
  z-index: 10;
  /* Ensure it doesn't interfere with other components */
  max-width: 300px; /* Limit width to available space */
  margin-top: 0.5rem; /* Add some spacing from logo */
}

.joke-ticker {
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.joke-ticker.active {
  opacity: 1;
}

.joke-text {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  white-space: nowrap;
  font-size: 1rem;
  font-weight: 600;
  color: #fbbf24;
  text-shadow: 0 0 8px rgba(251, 191, 36, 0.5);
  background: linear-gradient(90deg, #fbbf24, #f59e0b, #d97706);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: none;
  
  /* Fade effects */
  mask: linear-gradient(
    to right,
    transparent 0%,
    black 10%,
    black 90%,
    transparent 100%
  );
  -webkit-mask: linear-gradient(
    to right,
    transparent 0%,
    black 10%,
    black 90%,
    transparent 100%
  );
}


@keyframes gradient-sheen {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes gradientShift {
  0% {
    background-position: -100% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes gaugePulse {
  0% { 
    transform: rotate(-50deg);
  }
  20% { 
    transform: rotate(-30deg);
  }
  40% { 
    transform: rotate(-45deg);
  }
  60% { 
    transform: rotate(-25deg);
  }
  80% { 
    transform: rotate(-40deg);
  }
  100% { 
    transform: rotate(-50deg);
  }
}

@keyframes transitionSpin {
  0% { transform: rotate(0deg) scale(1); }
  25% { transform: rotate(90deg) scale(1.1); }
  50% { transform: rotate(180deg) scale(1.2); }
  75% { transform: rotate(270deg) scale(1.1); }
  100% { transform: rotate(360deg) scale(1); }
}

.nav-links {
  animation-duration: 0.5s;
  display: flex;
  gap: 1rem;
  align-items: center;
  /* Ensure bubbles are not clipped */
  overflow: visible;
}

.nav-link {
  color: var(--text-color);
  text-decoration: none;
  font-weight: 600;
  padding: 0.4rem 0.8rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

/* Override global nav a styles to remove underlines */
.nav-link::after {
  display: none !important;
}

.nav-link:hover {
  background-color: var(--card-background-hover);
  color: var(--text-color);
}

.nav-link.signup {
  background: var(--primary-accent);
  border: 1px solid var(--primary-accent);
  color: white !important;
}

.nav-link.signup:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  color: white !important;
}

.nav-link.profile-link {
  position: relative; /* Added for positioning the bubble */
  /* Ensure the bubble has enough space below */
  overflow: visible; /* Ensure bubble is not clipped */
  z-index: 1002; /* Higher than header z-index */
}

/* Dropdown Menu Styles */
.dropdown-menu {
  position: relative;
  display: inline-block;
}

.dropdown-toggle {
  background: transparent;
  border: 1px solid var(--border-color);
  cursor: pointer;
}

.dropdown-toggle:hover {
  background-color: var(--card-background-hover);
  border-color: var(--border-color);
}

.dropdown-arrow {
  margin-left: 0.5rem;
  transition: transform 0.2s ease;
  font-size: 0.75rem;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-content {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  min-width: 200px;
  z-index: 1002;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s ease;
  margin-top: 0.5rem;
  overflow: hidden;
  padding: 0;
}

.dropdown-open .dropdown-content {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: var(--text-color);
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  position: relative;
}

.dropdown-item:first-child {
  border-radius: var(--border-radius) var(--border-radius) 0 0;
  margin-top: 0;
}

.dropdown-item:last-child {
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  margin-bottom: 0;
}

.dropdown-item:only-child {
  border-radius: var(--border-radius);
}

.dropdown-item:hover {
  background-color: var(--card-background-hover);
}

/* Login button - use lava accent sparingly for primary action */
.dropdown-item.login {
  border-left: 2px solid var(--primary-accent);
}

.dropdown-item.login:hover {
  background-color: var(--card-background-hover);
  border-left-color: var(--primary-hover);
}

/* Logout button - use critical accent for destructive action */
.dropdown-item.logout {
  border-left: 2px solid var(--accent-critical);
}

.dropdown-item.logout:hover {
  background-color: var(--card-background-hover);
  border-left-color: var(--accent-critical);
}


.dropdown-item.admin-button {
  background-color: rgba(230, 126, 34, 0.1);
  border: 1px solid rgba(230, 126, 34, 0.3);
}

.dropdown-item.admin-button:hover {
  background-color: rgba(230, 126, 34, 0.2);
  border-color: rgba(230, 126, 34, 0.5);
}

.dropdown-item .nav-icon :deep(.icon) {
  width: 20px;
  height: 20px;
}

/* User avatar styling */
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
}

.profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-icon :deep(.icon) {
  width: 20px;
  height: 20px;
}

.dropdown-toggle .nav-icon :deep(.icon) {
  width: 20px;
  height: 20px;
}

.nav-text {
  display: inline;
}

@media (max-width: 768px) {
  .nav-text {
    display: none;
  }
  
  .dropdown-toggle .nav-text {
    display: none;
  }
  
  .dropdown-content {
    right: 0;
    left: auto;
    min-width: 180px;
  }
  
  .dropdown-item {
    padding: 0.625rem 0.875rem;
    font-size: 0.8125rem;
  }
  
  .dropdown-item .nav-text {
    display: inline;
  }
}

/* Horizontal separator line - only visible on mobile */
.header-separator {
  display: none; /* Hidden on desktop since date range summary is in top part */
  width: 100%;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent 0%,
    rgba(255, 255, 255, 0.1) 20%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 80%,
    transparent 100%
  );
  margin-bottom: 0.5rem; /* keep consistent spacing above tabs */
  flex-shrink: 0;
}

/* Mobile section for DateRangeSummary */
.header-mobile-section {
  display: none;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.5rem 2rem;
  justify-content: center;
  align-items: center;
  height: 30px;
  flex-shrink: 0;
}

/* DateRangeSummary responsive classes */
.date-range-summary-desktop {
  display: flex;
}

.date-range-summary-mobile {
  display: none;
}

/* Date Selector Wrapper */
.date-selector-wrapper {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem 1.25rem 2rem; /* Increased bottom padding from 0.5rem to 1.25rem (20px) */
  display: flex;
  justify-content: center;
}

/* Date Summary Animation */
.date-summary-enter-active,
.date-summary-leave-active {
  transition: all 0.4s ease;
}

.date-summary-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.date-summary-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.date-summary-enter-to,
.date-summary-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* Date Selector Animation */
.date-selector-enter-active,
.date-selector-leave-active {
  transition: all 0.3s ease;
}

.date-selector-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.date-selector-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.date-selector-enter-to,
.date-selector-leave-from {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 768px) {
  .header-content {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    max-width: 100%; /* Fill small screens */
  }
  
  .date-selector-wrapper {
    padding: 0 1rem 0.5rem 1rem;
  }
  
  .logo a {
    font-size: 1.5rem;
  }
  
  .nav-links {
    gap: 0.5rem;
  }
  
  .nav-link {
    padding: 0.3rem 0.6rem;
    font-size: 0.9rem;
  }
  
  /* Ensure login, signup, and pricing button text is always visible on mobile */
  .nav-link.login .nav-text,
  .nav-link.signup .nav-text,
  .nav-link.pricing .nav-text {
    display: inline !important;
  }
  
  /* Show mobile section and hide desktop DateRangeSummary */
  .header-mobile-section {
    display: flex !important;
  }
  
  .date-range-summary-desktop {
    display: none !important;
  }
  
  .date-range-summary-mobile {
    display: flex !important;
  }
  
  /* Show separator on mobile to separate top content from date range summary */
  .header-separator {
    display: block;
  }
  
  /* Adjust header height for mobile */
  .app-header {
    min-height: 95px;
  }
  
  .app-header.expanded {
    max-height: 280px; /* Increased space for mobile layout to avoid overlap */
  }
  
  .app-header.no-date-selectors {
    min-height: 60px; /* Normal height when date selectors are hidden on mobile */
    max-height: 60px;
  }
  
  /* Adjust joke ticker for mobile */
  .joke-ticker-container {
    max-width: 250px; /* Smaller width on mobile */
    margin-top: 0.25rem; /* Less spacing on mobile */
  }
  
  .joke-text {
    font-size: 0.9rem; /* Smaller text on mobile */
  }
}

@media (max-width: 480px) {
  .header-content {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  
  .header-mobile-section {
    padding: 0 0.5rem;
  }
  
  .date-selector-wrapper {
    padding: 0 0.5rem 1.25rem 0.5rem; /* Increased bottom padding to match desktop */
  }
  
  .logo a {
    font-size: 1.25rem;
  }
  
  .nav-links {
    gap: 0.25rem;
  }
  
  .nav-link {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
  }
  
  /* Ensure login, signup, and pricing button text is always visible on very small screens */
  .nav-link.login .nav-text,
  .nav-link.signup .nav-text,
  .nav-link.pricing .nav-text {
    display: inline !important;
  }
}
</style> 